import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Key, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PulseLogo } from '../components/SharedComponents';

const INPUT_STYLE = {
  width: '100%',
  background: 'var(--pp-overlay)',
  border: '1px solid var(--pp-border)',
  color: 'var(--pp-text)',
  padding: '12px 14px',
  fontSize: '15px',
  outline: 'none',
  fontFamily: 'inherit',
  borderRadius: 0,
};

const LABEL_STYLE = {
  display: 'block',
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--pp-text-3)',
  marginBottom: '8px',
};

export default function AuthPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();

  const isBenignError = (msg) => {
    if (!msg) return false;
    return /postMessage/i.test(msg) && /Request|could not be cloned|PerformanceServerTiming/i.test(msg);
  };

  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [passcode, setPasscode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [regStage, setRegStage] = useState('info'); // 'info' | 'passcode' | 'waitlisted'
  // Google OAuth state
  const [googleUserData, setGoogleUserData] = useState(null); // { name, email, picture }

  useEffect(() => {
    if (user) {
      // After authentication: land everyone on the dashboard. Subscriptions are offered when
      // the user taps "Subscribe" on a strategy — never forced on login.
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Listen for the google-waitlisted event from AuthContext
  useEffect(() => {
    const handler = (e) => {
      const data = e.detail;
      setGoogleUserData(data);
      setName(data.name || '');
      setEmail(data.email || '');
      setIsLogin(false);
      setRegStage('passcode');
    };
    window.addEventListener('google-waitlisted', handler);
    return () => window.removeEventListener('google-waitlisted', handler);
  }, []);

  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Please fill in all fields'); return; }
    setLoading(true);
    try {
      const data = await login(email, password);
      const u = data?.user || {};
      // Always land on dashboard; paywall only fires when user clicks Subscribe on a strategy.
      navigate('/dashboard');
      void u;
    } catch (e) {
      if (!isBenignError(e?.message)) setError(e.message || 'Invalid credentials');
    }
    setLoading(false);
  };

  const handleInfoSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name || !email || !password || !phone) { setError('Please fill in all fields'); return; }
    setLoading(true);
    try {
      const result = await register(email, password, name, phone);
      if (result?.waitlisted) { setRegStage('waitlisted'); setLoading(false); return; }
      if (result?.needs_passcode) { setRegStage('passcode'); setLoading(false); return; }
      if (result?.token) navigate('/dashboard');
    } catch (e) {
      if (!isBenignError(e?.message)) setError(e.message || 'Registration failed');
    }
    setLoading(false);
  };

  const handlePasscodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!passcode.trim()) { setError('Please enter your access passcode'); return; }
    setLoading(true);
    try {
      // Google OAuth user — they need a passcode but no password
      if (googleUserData) {
        const tempPassword = `google_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const result = await register(email, tempPassword, name, phone || '', passcode.trim());
        if (result?.passcode_error) { setError(result.message || 'Incorrect passcode.'); setLoading(false); return; }
        if (result?.token) navigate('/dashboard');
      } else {
        const result = await register(email, password, name, phone, passcode.trim());
        if (result?.passcode_error) { setError(result.message || 'Incorrect passcode.'); setLoading(false); return; }
        if (result?.token) navigate('/dashboard');
      }
    } catch (e) {
      if (!isBenignError(e?.message)) setError(e.message || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--pp-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ marginBottom: 48 }}>
          <PulseLogo size="lg" />
          <div style={{ width: 48, height: 2, background: 'var(--pp-primary)', margin: '12px 0' }} />
          <p className="section-label">Institutional-Grade Trading Algorithms</p>
        </div>

        {/* LOGIN */}
        {isLogin && (
          <form onSubmit={handleLogin}>
            <p style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.22em', color: 'var(--pp-text-2)', marginBottom: 24 }}>SIGN IN</p>

            <div style={{ marginBottom: 22 }}>
              <label style={LABEL_STYLE}>Email</label>
              <input data-testid="auth-email-input" style={INPUT_STYLE} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="trader@example.com" autoComplete="email" />
            </div>

            <div style={{ marginBottom: 22, position: 'relative' }}>
              <label style={LABEL_STYLE}>Password</label>
              <input data-testid="auth-password-input" style={INPUT_STYLE} type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" autoComplete="current-password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 12, bottom: 12, background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer', padding: 0 }}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {error && <p data-testid="auth-error" style={{ color: 'var(--pp-error)', fontSize: 13, marginBottom: 16 }}>{error}</p>}

            <button data-testid="auth-submit-button" type="submit" className="btn-primary" style={{ width: '100%', height: 52 }} disabled={loading}>
              {loading ? 'SIGNING IN...' : 'SIGN IN'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button data-testid="auth-forgot-password-link" type="button" onClick={() => navigate('/forgot-password')} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
                Forgot password?
              </button>
            </div>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
              <div style={{ flex: 1, height: 1, background: '#222' }} />
              <span style={{ color: '#444', fontSize: 11 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: '#222' }} />
            </div>

            {/* Sign up button */}
            <button
              data-testid="auth-switch-to-register"
              type="button"
              onClick={() => { setIsLogin(false); setRegStage('info'); setError(''); }}
              style={{ width: '100%', padding: 14, borderRadius: 10, background: 'transparent', border: '2px solid rgba(41,121,255,0.4)', color: '#2979FF', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginBottom: 10, letterSpacing: '0.5px' }}
            >
              Sign up &middot; Request Access
            </button>

            {/* Preview button — glowing green */}
            <button
              data-testid="demo-login-btn"
              type="button"
              onClick={async () => {
                try {
                  const hn = window.location.hostname;
                  const API_BASE = (hn === 'localhost' || hn === '127.0.0.1')
                    ? (process.env.REACT_APP_BACKEND_URL || window.location.origin)
                    : window.location.origin;
                  const resp = await fetch(`${API_BASE}/api/auth/demo-login`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
                  if (!resp.ok) { setError('Demo access temporarily unavailable'); return; }
                  const data = await resp.json();
                  if (data.token) { localStorage.setItem('auth_token', data.token); window.location.href = '/dashboard'; }
                  else { setError('Demo access temporarily unavailable'); }
                } catch (e) { console.error('Demo login error:', e); setError('Demo access temporarily unavailable'); }
              }}
              style={{ width: '100%', padding: 14, borderRadius: 10, background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.4)', color: '#00ff88', fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: '0 0 20px rgba(0,255,136,0.15)', textShadow: '0 0 8px rgba(0,255,136,0.5)' }}
            >
              Get Free Preview Access
            </button>
          </form>
        )}

        {/* REGISTER - STEP 1: Info */}
        {!isLogin && regStage === 'info' && (
          <form onSubmit={handleInfoSubmit}>
            <p style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.22em', color: 'var(--pp-text-2)', marginBottom: 8 }}>CREATE ACCOUNT</p>
            <p style={{ fontSize: 12, color: 'var(--pp-text-3)', marginBottom: 24, lineHeight: 1.6 }}>Fill in your details. You'll need an access passcode to complete signup.</p>

            <div style={{ marginBottom: 18 }}>
              <label style={LABEL_STYLE}>Full Name</label>
              <input data-testid="auth-name-input" style={INPUT_STYLE} type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={LABEL_STYLE}>Email</label>
              <input data-testid="auth-email-input" style={INPUT_STYLE} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="trader@example.com" />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={LABEL_STYLE}>Phone Number</label>
              <input data-testid="auth-phone-input" style={INPUT_STYLE} type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" />
            </div>
            <div style={{ marginBottom: 18, position: 'relative' }}>
              <label style={LABEL_STYLE}>Password</label>
              <input data-testid="auth-password-input" style={INPUT_STYLE} type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 12, bottom: 12, background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer', padding: 0 }}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {error && <p data-testid="auth-error" style={{ color: 'var(--pp-error)', fontSize: 13, marginBottom: 16 }}>{error}</p>}

            <button data-testid="auth-submit-button" type="submit" className="btn-primary" style={{ width: '100%', height: 52 }} disabled={loading}>
              {loading ? 'PROCESSING...' : 'CONTINUE'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <button type="button" onClick={() => { setIsLogin(true); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--pp-text-2)', fontSize: 13, cursor: 'pointer' }}>
                Already have an account? <span style={{ color: 'var(--pp-accent)', fontWeight: 700 }}>Sign In</span>
              </button>
            </div>
          </form>
        )}

        {/* REGISTER - STEP 2: Passcode */}
        {!isLogin && regStage === 'passcode' && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
              {/* Google user avatar if coming from Google OAuth */}
              {googleUserData?.picture ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
                  <img src={googleUserData.picture} alt={googleUserData.name} style={{ width: 64, height: 64, borderRadius: '50%', border: '2px solid var(--pp-border)', marginBottom: 10 }} />
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{googleUserData.name}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--pp-text-3)' }}>{googleUserData.email}</p>
                </div>
              ) : (
                <div style={{ width: 64, height: 64, background: 'rgba(155,35,53,0.15)', border: '1px solid rgba(155,35,53,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <Key size={28} color="var(--pp-primary)" />
                </div>
              )}
              <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--pp-text)', marginBottom: 8, textAlign: 'center' }}>
                {googleUserData ? 'Almost There!' : 'Enter Access Passcode'}
              </h2>
              <p style={{ fontSize: 13, color: 'var(--pp-text-2)', textAlign: 'center', lineHeight: 1.6 }}>
                {googleUserData
                  ? `Google verified! Enter your Profit Pulse access passcode to complete setup.`
                  : `Your information has been saved. Enter the passcode provided by your Profit Pulse contact to complete your account.`
                }
              </p>
            </div>

            <form onSubmit={handlePasscodeSubmit}>
              <div style={{ marginBottom: 22 }}>
                <label style={LABEL_STYLE}>Access Passcode</label>
                <input
                  data-testid="auth-passcode-input"
                  style={{ ...INPUT_STYLE, textAlign: 'center', fontSize: 20, letterSpacing: '0.3em', color: 'var(--pp-primary)' }}
                  type="text"
                  value={passcode}
                  onChange={e => { setPasscode(e.target.value); setError(''); }}
                  placeholder="Enter your passcode"
                  autoFocus
                />
              </div>

              {error && <p data-testid="auth-error" style={{ color: 'var(--pp-error)', fontSize: 13, marginBottom: 16 }}>{error}</p>}

              <button data-testid="auth-passcode-submit-btn" type="submit" className="btn-primary" style={{ width: '100%', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} disabled={loading}>
                <Key size={16} />
                {loading ? 'UNLOCKING...' : 'UNLOCK ACCOUNT'}
              </button>
            </form>

            <div style={{ background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', padding: 12, marginTop: 16, display: 'flex', gap: 8 }}>
              <Key size={14} color="var(--pp-text-3)" style={{ flexShrink: 0, marginTop: 2 }} />
              <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-text-3)', lineHeight: 1.6 }}>Don't have a passcode yet? We have your info and will reach out shortly.</p>
            </div>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button type="button" onClick={() => { setRegStage('info'); setPasscode(''); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--pp-text-2)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, margin: '0 auto' }}>
                <ArrowLeft size={14} /> Back
              </button>
            </div>
          </div>
        )}

        {/* REGISTER - Waitlisted */}
        {!isLogin && regStage === 'waitlisted' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>📋</div>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--pp-text)', marginBottom: 12 }}>You're on the list!</h2>
            <p style={{ fontSize: 14, color: 'var(--pp-text-2)', lineHeight: 1.7, marginBottom: 20 }}>
              We've saved your info for <span style={{ color: 'var(--pp-accent)' }}>{email}</span>.
              Your Profit Pulse contact will reach out with your access passcode shortly.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--pp-text-2)' }}>{name}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
                <span className="mono" style={{ fontSize: 13, color: 'var(--pp-text-2)' }}>{phone}</span>
              </div>
            </div>

            <button data-testid="auth-have-passcode-btn" onClick={() => setRegStage('passcode')} className="btn-primary" style={{ width: '100%', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
              <Key size={16} /> I HAVE A PASSCODE
            </button>

            <button type="button" onClick={() => { setIsLogin(true); setRegStage('info'); }} style={{ background: 'none', border: 'none', color: 'var(--pp-text-2)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, margin: '0 auto' }}>
              <ArrowLeft size={14} /> Back to Sign In
            </button>
          </div>
        )}

        <p style={{ fontSize: 10, color: 'var(--pp-text-3)', textAlign: 'center', marginTop: 48, lineHeight: 1.6 }}>
          Trading involves substantial risk of loss. Past performance does not guarantee future results.
        </p>
      </div>
    </div>
  );
}
